import { CATALOGUE_API_URL } from '@/lib/constants'
import { gql } from '@/lib/utils'
import { ApiDealer, Dealer, DealerWebsite } from '@/types/dealers'
import { Location } from '@/types/maps'
import { distanceInKm } from '@/lib/maps'

export const getDealers = async (): Promise<Dealer[]> => {
  const headers = { accept: 'application/json', 'content-type': 'application/json' }
  const body = JSON.stringify({ variables: { language: 'no', path: '/dealers' }, query: dealersQuery })
  const response = await fetch(CATALOGUE_API_URL, { method: 'POST', headers, body })

  if (!response.ok) throw new Error(await response.text())
  const json = await response.json()
  if (json.errors) throw new Error(JSON.stringify(json.errors))
  const dealers = json.data.catalogue.children as ApiDealer[]
  return dealers.filter((dealer) => !!dealer.postalCode.content).map(apiDealerToDealer)
}

export const getDealerWebsites = async (): Promise<DealerWebsite[]> => {
  const baseUrl = typeof window === 'undefined' ? process.env.NEXT_PUBLIC_VERCEL_URL : ''
  const res = await fetch(`${baseUrl}/dealer-websites.json`)
  const json = await res.text()
  return JSON.parse(json)
}

export const findNearestDealer = (location: Location, dealers: Dealer[]): { dealer: Dealer; distance: number } => {
  const firstDealer = dealers[0]!
  return dealers.reduce<{ dealer: Dealer; distance: number }>(
    (nearest, dealer) => {
      const distance = distanceInKm(location, dealer.location)
      if (distance < nearest.distance) return { dealer, distance }
      return nearest
    },
    { dealer: firstDealer, distance: distanceInKm(location, firstDealer.location) },
  )
}

const apiDealerToDealer = (dealer: ApiDealer): Dealer => ({
  name: dealer.name,
  address: dealer.address.content.text,
  postalCode: dealer.postalCode.content?.text ?? '',
  city: dealer.city.content.text,
  location: {
    latitude: dealer.location.content?.lat ?? 0,
    longitude: dealer.location.content?.long ?? 0,
  },
  mapsUrl: `https://www.google.com/maps?${new URLSearchParams({
    q: `${dealer.name}, ${dealer.address.content.text}, ${dealer.postalCode.content?.text ?? ''} ${dealer.city.content.text}`,
  })}`,
})

export const formatDealer = (dealer: Dealer) => `${dealer.name}, ${dealer.address}, ${dealer.postalCode} ${dealer.city}`

const dealersQuery = gql`
  query dealers {
    catalogue(path: "/dealers", language: "no") {
      children {
        path
        name
        components {
          id
          content {
            ... on BooleanContent {
              value
            }
          }
        }
        address: component(id: "address") {
          content {
            ... on SingleLineContent {
              text
            }
          }
        }
        city: component(id: "city") {
          content {
            ... on SingleLineContent {
              text
            }
          }
        }
        postalCode: component(id: "postal-code") {
          content {
            ... on SingleLineContent {
              text
            }
          }
        }
        location: component(id: "coordinates") {
          content {
            ... on LocationContent {
              lat
              long
            }
          }
        }
      }
    }
  }
`
